import React, { useEffect, useRef } from 'react'
import { Joyride, EventData, STATUS, EVENTS, ACTIONS } from 'react-joyride'
import { useNavigate, useLocation } from 'react-router-dom'
import { useWebTour } from '@/designs/new/contexts/WebTourContext'
import { WEB_TOUR_STEPS } from '@/designs/new/constants/webTourSteps'
import CustomModal from './CustomModal'
import { useAppSelector } from '@/hooks/storeHooks/storehooks'
import { isStepAllowed, findNextAuthorizedIndex } from './webTourUtils'
import { toast } from 'sonner'

// ---------------------------------------------------------------------------
// Custom Tooltip styles — matches the portal design system
// ---------------------------------------------------------------------------

// const JOYRIDE_STYLES = {
//   options: {
//     primaryColor: '#1A56DB', // info / brand blue
//     backgroundColor: '#FFFFFF',
//     textColor: '#1E293B',
//     arrowColor: '#FFFFFF',
//     overlayColor: 'rgba(15, 23, 42, 0.55)',
//     zIndex: 99999,
//     width: 360,
//   },
//   buttonNext: {
//     backgroundColor: '#1A56DB',
//     borderRadius: '0.5rem',
//     color: '#FFFFFF',
//     fontSize: '0.875rem',
//     fontWeight: 600,
//     padding: '0.5rem 1.25rem',
//     outline: 'none',
//   },
//   buttonBack: {
//     color: '#64748B',
//     fontSize: '0.875rem',
//     fontWeight: 500,
//     marginRight: '0.5rem',
//   },
//   buttonSkip: {
//     color: '#64748B',
//     fontSize: '0.8125rem',
//   },
//   buttonClose: {
//     color: '#64748B',
//   },
//   tooltip: {
//     borderRadius: '0.75rem',
//     boxShadow: '0 10px 40px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.1)',
//     padding: '1.25rem 1.5rem',
//     fontFamily: 'inherit',
//   },
//   tooltipTitle: {
//     fontSize: '1rem',
//     fontWeight: 700,
//     color: '#0F172A',
//     marginBottom: '0.375rem',
//   },
//   tooltipContent: {
//     fontSize: '0.875rem',
//     color: '#475569',
//     lineHeight: 1.6,
//   },
//   tooltipFooter: {
//     marginTop: '1rem',
//   },
// }

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const WebTour: React.FC = () => {
  const { isTourRunning, tourStepIndex, stopTour, setTourStepIndex } =
    useWebTour()
  const navigate = useNavigate()
  const location = useLocation()
  const allowedPaths = useAppSelector(state => state.menu.paths)

  // Keep a ref so we can compare route in the callback without stale closures
  const navigationPendingRef = useRef(false)
  const prevStepIndexRef = useRef(-1)

  // Reset prevStepIndexRef when the tour is stopped
  useEffect(() => {
    if (!isTourRunning) {
      prevStepIndexRef.current = -1
    }
  }, [isTourRunning])

  // Navigate to the required route when the step index changes
  useEffect(() => {
    if (!isTourRunning || allowedPaths.length === 0) return

    const step = WEB_TOUR_STEPS[tourStepIndex]
    if (!step) return

    const prevIndex = prevStepIndexRef.current
    prevStepIndexRef.current = tourStepIndex

    // Check if current step is allowed
    if (!isStepAllowed(step, allowedPaths)) {
      const direction =
        prevIndex !== -1 && tourStepIndex < prevIndex ? 'backward' : 'forward'
      const nextIndex = findNextAuthorizedIndex(
        tourStepIndex,
        direction,
        allowedPaths,
      )

      if (nextIndex !== -1) {
        setTourStepIndex(nextIndex)
      } else {
        stopTour()
        toast.info('The tour has ended as there are no more accessible pages.')
      }
      return
    }

    if (!step.route) return

    // Build the route with the app basename stripped if present
    const baseName = '/app'
    const targetPath = step.route

    // Current path without the basename prefix
    const currentPath = location.pathname.startsWith(baseName)
      ? location.pathname.slice(baseName.length)
      : location.pathname

    if (currentPath !== targetPath) {
      navigationPendingRef.current = true
      navigate(targetPath)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourStepIndex, isTourRunning, allowedPaths])

  // After navigating to a new page, give the DOM 700ms to fully mount
  // before Joyride tries to find the target element.
  const [joyrideReady, setJoyrideReady] = React.useState(true)

  useEffect(() => {
    if (navigationPendingRef.current) {
      setJoyrideReady(false)
      navigationPendingRef.current = false
      const timer = setTimeout(() => setJoyrideReady(true), 1200)
      return () => clearTimeout(timer)
    }
  }, [location.pathname])

  const handleCallback = (data: EventData) => {
    const { status, type, action, index } = data

    // Tour finished or skipped
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      stopTour()
      return
    }

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1
      const direction = action === ACTIONS.PREV ? 'backward' : 'forward'

      const targetIndex = findNextAuthorizedIndex(
        nextIndex,
        direction,
        allowedPaths,
      )
      if (targetIndex !== -1 && targetIndex < WEB_TOUR_STEPS.length) {
        setTourStepIndex(targetIndex)
      } else {
        stopTour()
        if (direction === 'forward') {
          toast.success('You have completed the tour!')
        }
      }
    }
  }

  if (!isTourRunning) return null

  return (
    <Joyride
      tooltipComponent={CustomModal}
      steps={WEB_TOUR_STEPS}
      stepIndex={tourStepIndex}
      run={isTourRunning && joyrideReady}
      continuous
      scrollToFirstStep
      onEvent={handleCallback}
      floatingOptions={{
        hideArrow: true,
      }}
      // styles={JOYRIDE_STYLES}
      // options={{
      //   buttons: ['back', 'close', 'primary', 'skip'],
      //   showProgress: true,
      //   overlayClickAction: false,
      //   blockTargetInteraction: true,
      // }}
      // locale={{
      //   back: '← Back',
      //   close: '✕',
      //   last: 'Finish 🎉',
      //   next: 'Next →',
      //   skip: 'Skip tour',
      // }}
    />
  )
}

export default WebTour
